import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Package, Tags } from "lucide-react";

const EMPTY = {
  name: "", category: "diesel", unit: "L", hsn_code: "27101930",
  gst_applicable: true, gst_rate: 18, cess_rate: 0, default_price: 95, description: "",
};

export default function Products() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/products/${editingId}`, form) : api.post("/products", form)),
    onSuccess: () => { toast.success(editingId ? "Product updated" : "Product added"); qc.invalidateQueries({ queryKey: ["products"] }); setOpen(false); setEditingId(null); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => { toast.success("Deactivated"); qc.invalidateQueries({ queryKey: ["products"] }); },
  });

  const openEdit = (p) => { setForm({ ...p }); setEditingId(p.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Products & Services</h1>
          <p className="text-sm text-muted-foreground">Master product list with GST/HSN configuration. GST/non-GST applicability per product.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-product"><Plus size={14} className="mr-1" /> New product</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Product name</Label><Input value={form.name} onChange={upd("name")} data-testid="p-name" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={upd("category")}>
                  <SelectTrigger data-testid="p-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diesel">Diesel (HSD)</SelectItem>
                    <SelectItem value="petrol">Petrol</SelectItem>
                    <SelectItem value="lubricant">Lubricant</SelectItem>
                    <SelectItem value="additive">Additive</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={upd("unit")}>
                  <SelectTrigger data-testid="p-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Litre</SelectItem>
                    <SelectItem value="KG">Kilogram</SelectItem>
                    <SelectItem value="NOS">Nos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>HSN / SAC code</Label><Input value={form.hsn_code} onChange={upd("hsn_code")} data-testid="p-hsn" /></div>
              <div><Label>Default price (₹)</Label><Input type="number" step="0.01" value={form.default_price} onChange={upd("default_price")} data-testid="p-price" /></div>
              <div className="col-span-2 flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                <div>
                  <div className="text-sm font-semibold">GST applicable</div>
                  <div className="text-xs text-muted-foreground">Turn off for GST-exempt items</div>
                </div>
                <Switch checked={!!form.gst_applicable} onCheckedChange={upd("gst_applicable")} data-testid="p-gst-applicable" />
              </div>
              <div>
                <Label>GST rate (%)</Label>
                <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
                  <SelectTrigger data-testid="p-gst-rate"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Cess rate (%)</Label><Input type="number" step="0.01" value={form.cess_rate} onChange={upd("cess_rate")} /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={upd("description")} /></div>
            </div>
            <DialogFooter>
              <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="btn-save-product">{save.isPending ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">HSN</th>
                <th className="px-4 py-2 text-right">Price</th>
                <th className="px-4 py-2 text-right">GST</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((p) => (
                <tr key={p.id} className="border-b border-border/40 text-sm" data-testid={`product-row-${p.code}`}>
                  <td className="px-4 py-2 font-mono">{p.code}</td>
                  <td className="px-4 py-2 font-semibold">{p.name}</td>
                  <td className="px-4 py-2 text-xs uppercase">{p.category}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.hsn_code || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">₹{p.default_price?.toFixed(2)}/{p.unit}</td>
                  <td className="px-4 py-2 text-right text-xs">
                    {p.gst_applicable ? <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">{p.gst_rate}%</span> : <span className="text-muted-foreground">EXEMPT</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove.mutate(p.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
              {(data?.items || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <Package size={32} className="mx-auto mb-2 opacity-40" />
                  No products yet. Add your first product to enable GST-aware invoicing.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
